import React, { useState, useEffect } from 'react';
import type { Medication } from '../models/diary';
import type { MedicationCatalog } from '../models/medication';
import { diaryService } from '../services/diaryService';
import SearchableSelect from './SearchableSelect';

interface MedicationItemFormProps {
  medications: Medication[];
  patientUserId?: string | number;
  onMedicationsChange: (medications: Medication[]) => void;
  onNameChange: (name: string) => void;
  isEditMode?: boolean;
  onDelete?: () => void;
}

const MedicationItemForm: React.FC<MedicationItemFormProps> = ({
  medications,
  patientUserId,
  onMedicationsChange,
  onNameChange,
  isEditMode = false,
  onDelete
}) => {
  const [medicationCatalog, setMedicationCatalog] = useState<MedicationCatalog[]>([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);

  // Fetch medication catalog when component mounts
  useEffect(() => {
    if (patientUserId) {
      const fetchCatalog = async () => {
        setLoadingCatalogs(true);
        try {
          const catalog = await diaryService.getMedicationCatalog();
          setMedicationCatalog(catalog);
        } catch (error) {
          console.error('Error fetching medication catalog:', error);
        } finally {
          setLoadingCatalogs(false);
        }
      };

      fetchCatalog();
    }
  }, [patientUserId]);

  // Initialize with one empty medication if needed
  useEffect(() => {
    if (medications.length === 0) {
      onMedicationsChange([{ description: '' } as Medication]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get the single medication (we always work with index 0)
  const medication = medications[0] || { description: '' };

  const handleCatalogChange = (catalogId: number) => {
    const catalog = medicationCatalog.find(c => c.id === catalogId);
    if (catalog) {
      const updatedMedication: Medication = {
        ...medication,
        medicationCatalog: catalog
      };
      onMedicationsChange([updatedMedication]);
      onNameChange(catalog.name);
    }
  };

  const handleDescriptionChange = (description: string) => {
    const updatedMedication: Medication = {
      ...medication,
      description
    };
    onMedicationsChange([updatedMedication]);
  };

  return (
    <div style={{ marginBottom: 20 }}>
      {/* Section Header */}
      <div style={{
        marginBottom: 16,
        paddingBottom: 12,
        borderBottom: '2px solid #e5e7eb'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <h3 style={{
              margin: 0,
              fontSize: 16,
              fontWeight: 600,
              color: '#1f2937'
            }}>
              Medicamento
            </h3>
            <p style={{
              margin: '4px 0 0 0',
              fontSize: 13,
              color: '#6b7280'
            }}>
              Selecciona el medicamento del catálogo y define las instrucciones de administración
            </p>
          </div>
          {isEditMode && onDelete && (
            <button
              type="button"
              onClick={onDelete}
              style={{
                background: '#fee2e2',
                color: '#991b1b',
                border: 'none',
                borderRadius: 6,
                padding: '8px 16px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'background 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#fecaca'}
              onMouseLeave={e => e.currentTarget.style.background = '#fee2e2'}
            >
              <span>🗑️</span>
              <span>Eliminar</span>
            </button>
          )}
        </div>
      </div>

      {/* Catalog Selection */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block' }}>
          <span style={{
            fontSize: 13,
            fontWeight: 600,
            color: '#374151',
            marginBottom: 8,
            display: 'block'
          }}>
            1. Medicamento del catálogo *
          </span>
          {loadingCatalogs ? (
            <div style={{
              padding: 12,
              fontSize: 13,
              color: '#6b7280',
              backgroundColor: '#f9fafb',
              borderRadius: 6,
              border: '1px dashed #d1d5db',
              textAlign: 'center'
            }}>
              Cargando catálogo...
            </div>
          ) : (
            <SearchableSelect
              options={medicationCatalog.map(catalog => ({
                value: catalog.id,
                label: catalog.name,
                searchText: `${catalog.name} ${catalog.description || ''}`
              }))}
              value={(medication as any).medicationCatalog?.id || ''}
              onChange={(value) => handleCatalogChange(Number(value))}
              placeholder="-- Selecciona un medicamento --"
              hasError={!(medication as any).medicationCatalog?.id}
            />
          )}
        </label>
      </div>

      {/* Catalog Info Display */}
      {(medication as any).medicationCatalog && (
        <div style={{
          backgroundColor: '#fff7ed',
          border: '1px solid #fed7aa',
          borderRadius: 6,
          padding: 12,
          marginBottom: 16
        }}>
          <div style={{
            fontSize: 12,
            fontWeight: 600,
            color: '#c2410c',
            marginBottom: 6
          }}>
            ℹ️ Información del medicamento
          </div>
          {(medication as any).medicationCatalog.description && (
            <div style={{
              fontSize: 12,
              color: '#374151',
              marginBottom: 8,
              lineHeight: '1.5'
            }}>
              {(medication as any).medicationCatalog.description.length > 200
                ? `${(medication as any).medicationCatalog.description.substring(0, 200)}...`
                : (medication as any).medicationCatalog.description}
            </div>
          )}
          {(medication as any).medicationCatalog.purchaseUrl && (
            <a
              href={(medication as any).medicationCatalog.purchaseUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: 12,
                color: '#f97316',
                textDecoration: 'none',
                fontWeight: 500,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4
              }}
            >
              🔗 Ver enlace →
            </a>
          )}
        </div>
      )}

      {/* Dosage Instructions */}
      <div>
        <label style={{ display: 'block' }}>
          <span style={{
            fontSize: 13,
            fontWeight: 600,
            color: '#374151',
            marginBottom: 6,
            display: 'block'
          }}>
            2. Instrucciones de administración *
          </span>
          <span style={{
            fontSize: 12,
            color: '#6b7280',
            marginBottom: 8,
            display: 'block'
          }}>
            Especifica cómo y cuándo debe tomar el medicamento
          </span>
          <textarea
            value={medication.description || ''}
            onChange={e => handleDescriptionChange(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: medication.description ? '1px solid #d1d5db' : '2px solid #fbbf24',
              borderRadius: 6,
              fontSize: 13,
              minHeight: 70,
              resize: 'vertical',
              fontFamily: 'inherit',
              outline: 'none',
              boxSizing: 'border-box'
            }}
            placeholder="Ej: 1 comprimido al día por la mañana con el desayuno"
          />
        </label>
      </div>
    </div>
  );
};

export default MedicationItemForm;
